<template>
  <ConfigurationHeader
    :canCreate="true"
    :endPointData="endPointData"
    loadScreen="equipmentTypeConfiguration"
    :subNavLinks="subNavLinks"
    :textData="textData"
    @search="searchWord"
    @show-search="toggleSearch"
    @sort="sortType"
  />

  <HeaderBar
    v-if="searchResults"
    :modifiers="['narrow', isDesktop() && 'no-padding']"
  >
    <p class="configuration-info typo-ignore typo-small-title">
      <strong>{{ $t(endPointData.nav) }}</strong>
      <span class="typo-tiny">{{ $t("shafts.configured", { total: searchResults.length }) }}</span>
    </p>
  </HeaderBar>

  <div
    :class="[
      {
        'configuration-wrapper--padding': !isDesktop(),
        'configuration-wrapper--default-normal': !isHme && !showSearch,
        'configuration-wrapper--default-small': !isHme && showSearch,
        'configuration-wrapper--large-normal': isHme && !showSearch,
        'configuration-wrapper--large-small': isHme && showSearch,
      }, joinClasses('configuration-wrapper', modifiers)
    ]"
  >
    <template
      v-for="type in searchResults"
      :key="type.id"
    >
      <ConfigurationItem
        :endpointIdentifier="endPointData.endpointIdentifier"
        :listItem="type"
        :operationType="endPointData.operationType"
        :organisationId="$route.params.organisationId"
        @delete-item="deleteItem"
      />
    </template>
  </div>

  <div class="configuration-new hide-for-medium">
    <router-link
      class="button"
      :to="{ name: endPointData.newFormRoute }"
    >
      <div class="button__icon button--margin-right">
        <PlusIcon />
      </div>
      {{ textData.new }}
    </router-link>
  </div>
</template>
<script setup>
import { ref, onMounted, watch, computed } from 'vue';

// Utils
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';
import orderBy from '@/functions/order_by';
import useAjax from '@/functions/use_ajax';
import joinClasses from '@/functions/join_classes';
import isDesktop from '@/functions/responsive';

// Components
import Searcher from '@/functions/searcher';
import HeaderBar from '@/components/generic/header_bar.vue';
import ConfigurationItem from '@/components/generic/configuration_item.vue';
import ConfigurationHeader from '@/components/generic/configuration_header_template.vue';

// Svg
import PlusIcon from '@/images/plus-16.svg';

const props = defineProps({
  /** Hash containing endpoints */
  endPointData: { type: Object, required: true, default: null },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
  /** Additional classes you may add */
  modifiers: { type: Array, default: () => [] },
});

const route = useRoute();
const ajax = useAjax();
const search = ref(null);
const searchResults = ref(null);
const loading = ref(null);
const sortBy = ref(null);
const showSearch = ref(false);
const typeList = ref([]);
let searcher;
let searchKeys = ['name'];
const byName = orderBy('name', x => x.toLowerCase());
const isHme = computed(() => route.name === 'equipmentTypeConfiguration');

const subNavLinks = computed(() => {
  const links = [
    {
      label: I18n.t('configuration.process.nav.processes'),
      value: 'equipmentProcessConfiguration',
      description: I18n.t('configuration.process.process_description')
    },
    {
      label: I18n.t('configuration.type.nav.types'),
      value: 'equipmentTypeConfiguration',
      description: I18n.t('configuration.type.type_description')
    },
  ];

  return [links[0].value, links[1].value].includes(route.name) ? links : null;
});

onMounted(() => {
  loadType();
});

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => sortResults());

watch(() => typeList, buildSearcher);

watch(() => props.endPointData, loadType);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher?.search(search.value);
  } else {
    searchResults.value = typeList.value;
  }

  sortResults();
});

function sortResults() {
  let sortFn;
  switch (sortBy.value) {
    case 'alphabetical': sortFn = byName.asc; break;
    case 'alphabetical_reverse': sortFn = byName.desc; break;
  }
  searchResults.value.sort(sortFn);
}

function buildSearcher(items = typeList.value) {
  searcher = new Searcher(items, { keys: searchKeys });
}

async function loadType() {
  if (route.params.organisationId) {
    loading.value = true;
    let { ok, data } = await ajax(props.endPointData.typeGroupsEndpoint);

    if (ok) {
      typeList.value = data;
      searchResults.value = data;
      loading.value = false;
      buildSearcher();
    }
  }
}

function deleteItem(event) {
  typeList.value = typeList.value.filter(item => item.id !== event);
  searchResults.value = typeList.value;
}

function sortType(data) {
  sortBy.value = data;
}

function searchWord(data) {
  search.value = data;
}

function toggleSearch(data) {
  showSearch.value = data;
}
</script>
